import { App } from "antd";
import { createElement, useCallback, useState } from "react";
import { ApiError, api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";

/**
 * Prints a PDF from a hidden frame, so a packing desk printing one label per
 * parcel does not pile up browser tabs. Browsers that cannot print a PDF from
 * a frame get it in a new tab instead.
 */
export function printPdf(blob: Blob): boolean {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.src = url;
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      window.open(url, "_blank");
    }
    // Keep the frame until the print dialog is long done.
    setTimeout(() => {
      frame.remove();
      URL.revokeObjectURL(url);
    }, 60_000);
  };
  document.body.appendChild(frame);
  return true;
}

/**
 * The label of a packed parcel, printed from the desk. The server books the
 * courier first if the order is not arranged yet.
 */
export function usePackLabel() {
  const { t } = useI18n();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const [printing, setPrinting] = useState<string | null>(null);
  // A manual order has no courier to book: the packer types its tracking number in.
  const askTracking = useCallback(
    (externalId: string) =>
      new Promise<string | null>((resolve) => {
        let value = "";
        modal.confirm({
          title: t("wms.pack.trackingTitle", { number: externalId }),
          content: createElement("input", {
            className: "ant-input z-num",
            autoFocus: true,
            maxLength: 60,
            placeholder: t("manual.trackingNo"),
            onChange: (e: { target: { value: string } }) => (value = e.target.value),
          }),
          okText: t("common.save"),
          cancelText: t("common.cancel"),
          onOk: () => resolve(value.trim() || null),
          onCancel: () => resolve(null),
        });
      }),
    [modal, t],
  );
  const print = useCallback(
    async (orderId: string, externalId: string): Promise<boolean> => {
      const label = () => unwrap(api.POST("/v1/packing/orders/{orderID}/label", { params: { path: { orderID: orderId } }, parseAs: "blob" }));
      setPrinting(orderId);
      try {
        let blob: Blob;
        try {
          blob = await label();
        } catch (err) {
          if (!(err instanceof ApiError && err.code === "manual_tracking_required")) throw err;
          const trackingNo = await askTracking(externalId);
          if (!trackingNo) return false;
          await unwrap(api.POST("/v1/orders/{orderID}/tracking", { params: { path: { orderID: orderId } }, body: { trackingNo } }));
          blob = await label();
        }
        printPdf(blob);
        return true;
      } catch (err) {
        void message.error(`${t("wms.pack.labelFailed", { number: externalId })} ${errorText(err)}`);
        return false;
      } finally {
        setPrinting(null);
      }
    },
    [askTracking, errorText, message, t],
  );
  return { print, printing };
}
